
import {
  CircleUser,
  Heart,
  LifeBuoy,
  LogOut,
  Package,
  User,
  ChevronRight,
  X,
} from "lucide-react";
import {
  DrawerClose,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@socialflows/shared-lib";
import {
  VerticalDrawer,
  VerticalDrawerContent,
} from "@socialflows/shared-lib";
import { AuthDrawer } from "./auth/auth-drawer";
import { Link } from "react-router-dom";
import { IconButton } from "./icon-button";
import { useAuth } from "@/contexts/auth-context";

const menuItems = [
  {
    icon: CircleUser,
    label: "My Profile",
    href: "/profile",
    description: "View and edit your profile",
  },
  {
    icon: Package,
    label: "My Orders",
    href: "/orders",
    description: "Track your orders",
  },
  {
    icon: Heart,
    label: "Wishlist",
    href: "/wishlist",
    description: "Your saved items",
  },
];

export const UserMenu = () => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();

  if (isLoading) {
    return (
      <IconButton className="cursor-pointer">
        <User className="h-5 w-5" strokeWidth={1.5} />
      </IconButton>
    );
  }

  if (!isAuthenticated) {
    return <AuthDrawer />;
  }

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const userInitials = user
    ? `${user.firstName?.charAt(0) || ""}${user.lastName?.charAt(0) || ""}`.toUpperCase()
    : "U";

  return (
    <VerticalDrawer>
      <DrawerTrigger>
        <div className="relative p-2 text-foreground/80 hover:text-foreground transition-colors duration-200">
          <User className="h-5 w-5" strokeWidth={1.5} />
          <span className="sr-only">Toggle user menu</span>
        </div>
      </DrawerTrigger>
      <VerticalDrawerContent>
        <DrawerHeader className="px-4 border-b">
          <DrawerTitle>
            <div className="flex items-center uppercase">
              My Account
              <DrawerClose className="cursor-pointer ml-auto mr-0">
                <X className="w-6 h-6 text-muted-foreground hover:text-foreground transition-colors" />
              </DrawerClose>
            </div>
          </DrawerTitle>
        </DrawerHeader>

        {/* User Profile Section */}
        <div className="p-4 bg-muted/30">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-lg">
              {userInitials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="flex-1 py-2">
          {menuItems.map((item) => (
            <DrawerClose key={item.href} asChild>
              <Link
                to={item.href}
                className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center group-hover:bg-muted transition-colors">
                  <item.icon className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground">{item.label}</p>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
              </Link>
            </DrawerClose>
          ))}

          <DrawerClose asChild>
            <Link
              to="/support"
              className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center group-hover:bg-muted transition-colors">
                <LifeBuoy className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground">Help & Support</p>
                <p className="text-sm text-muted-foreground">Get assistance</p>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors" />
            </Link>
          </DrawerClose>
        </div>

        {/* Logout Button */}
        <div className="p-4 border-t mt-auto">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-border hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogOut className="h-5 w-5" strokeWidth={1.5} />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </VerticalDrawerContent>
    </VerticalDrawer>
  );
};
